import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FilterMenu from '../../components/FilterMenu';

import moment from 'moment';

import ReportGrid from '../../components/ReportGrid';
import SortMenuButton from '../../components/SortMenuButton';
import MonthlyComparisonChart from './MonthlyComparisonChart';

import useMonthlyExpense from './useMonthlyExpense';
import useTransactions from './useTransactions';
import useIncomeReport from './useIncomeReport';
import useExpenseReport from './useExpenseReport';
import useSankeyData from './useSankeyData';
import SankeyChart from './SankeyChart';

import { YEAR_LIST, MONTH_LIST } from '../../constants';

const MonthlyExpense = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const accountList = useSelector((state) => state.accountList);
	const allAccountsTransactions = useSelector((state) => state.allAccountsTransactions);
	const { exchangeRate, currency, livingExpenseExempt = [] } = useSelector((state) => state.settings);
	const [year, setYear] = useState(() => {
		const params = new URLSearchParams(location.search);
		const y = parseInt(params.get('year'), 10);
		return y || parseInt(moment().format('YYYY'), 10);
	});
	const [filters, setFilters] = useState(() => {
		const params = new URLSearchParams(location.search);
		const filtersParam = params.get('filters');
		return filtersParam ? filtersParam.split(',') : [];
	});
	const [view, setView] = useState('grid');

	const reportView = filters.includes('category') ? 'category' : 'subcategory';
	const usd = currency === 'USD';

	const livingExpenseOnly = filters.includes('livingExpenseOnly');
	const livingExpenseCardOnly = filters.includes('livingExpenseCardOnly');
	const boAOnly = filters.includes('boAOnly');

	const { incomeTransactions, expenseTransactions } = useTransactions(allAccountsTransactions, livingExpenseCardOnly, boAOnly);
	const { incomeReport, totalMonthIncomeSum, totalIncomeSum } = useIncomeReport(accountList, incomeTransactions, year, usd, exchangeRate, reportView);
	const { expenseReport, totalMonthExpenseSum, totalExpenseSum } = useExpenseReport(accountList, expenseTransactions, year, livingExpenseOnly, usd, exchangeRate, reportView, livingExpenseExempt);
	const reportData = useMonthlyExpense(incomeReport, expenseReport, totalMonthIncomeSum, totalIncomeSum, totalMonthExpenseSum, totalExpenseSum, year);
	const { sankeyData } = useSankeyData(incomeReport, expenseReport, totalIncomeSum, totalExpenseSum);

	const chartData = MONTH_LIST.map((_, index) => ({
		month: moment().month(index).format('MMM'),
		income: totalMonthIncomeSum[index] || 0,
		expense: Math.abs(totalMonthExpenseSum[index] || 0)
	}));

	const onYearChange = event => {
		const val = event.target.value;
		setYear(val);
		const params = new URLSearchParams(location.search);
		params.set('year', val);
		navigate(`?${params.toString()}`, { replace: true });
	};

	const onFilterChange = (newFilters) => {
		setFilters(newFilters);
		const params = new URLSearchParams(location.search);
		if (newFilters.length > 0) {
			params.set('filters', newFilters.join(','));
		} else {
			params.delete('filters');
		}
		navigate(`?${params.toString()}`, { replace: true });
	};

	const filterOptions = [
		{ value: 'category', label: '카테고리별 보기' },
		{ value: 'livingExpenseOnly', label: '생활비만 보기' },
		{ value: 'livingExpenseCardOnly', label: '생활비카드만 보기' },
		{ value: 'boAOnly', label: 'BoA Only' }
	];

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, p: 1 }}>
			<Stack direction="row" justifyContent="space-between" alignItems="center">
				<FormControl size="small" sx={{ minWidth: 150 }}>
					<Select
						value={year}
						onChange={onYearChange}
					>
						{
							YEAR_LIST.map(i => (
								<MenuItem key={i.key} value={i.value}>{i.text}</MenuItem>
							))
						}
					</Select>
				</FormControl>
				<div>
					<Stack direction="row" spacing={1} alignItems="center">
						<SortMenuButton
							value={view}
							onChange={setView}
							options={[
								{ value: 'grid', label: 'Grid' },
								{ value: 'sankey', label: 'Sankey' }
							]}
						/>
						<FilterMenu
							filterName="Filters"
							options={filterOptions}
							selectedOptions={filters}
							onSelectionChange={onFilterChange}
						/>
					</Stack>
				</div>
			</Stack>
			<MonthlyComparisonChart chartData={chartData} />
			<Box sx={{ flex: 1, mt: { xs: 0, md: 1 } }}>
				{view === 'grid' && reportData.length > 1 && <ReportGrid reportData={reportData} supportSearch/>}
				{view === 'sankey' && <SankeyChart data={sankeyData} />}
			</Box>
		</Box>
	);
};

export default MonthlyExpense;
